import { useState } from "react";
import { useFetch, apiPost, timeAgo } from "@/lib/shieldx";
import { PageHeader, Loader, Panel } from "@/components/shieldx/Primitives";
import { StatusBadge, RiskBadge, DemoTag } from "@/components/shieldx/Badges";
import { Table } from "@/components/shieldx/Table";
import { Boxes, ShieldCheck, Eye, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const ACTIONS = [
  { key: "PROTECT", label: "Protect", icon: ShieldCheck },
  { key: "MONITOR", label: "Monitor", icon: Eye },
  { key: "ISOLATE", label: "Isolate", icon: Ban },
  { key: "RECOVER", label: "Recover", icon: RotateCcw },
];

export default function Assets() {
  const { data, loading, reload } = useFetch("/assets");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  if (loading || !data) return <Loader label="Loading asset registry" />;

  const act = async (action) => {
    setBusy(true);
    try {
      await apiPost(`/assets/${sel.id}/action`, { action });
      toast.success(`Asset ${action.toLowerCase()} applied`);
      setSel(null); reload();
    } catch (e) { toast.error("Action failed"); } finally { setBusy(false); }
  };

  const columns = [
    { key: "name", label: "Asset", render: (r) => (
      <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.id}</p></div>
    ) },
    { key: "type", label: "Type" },
    { key: "environment", label: "Env", render: (r) => <span className="font-mono text-xs text-slate-400">{r.environment}</span> },
    { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> },
    { key: "state", label: "State", render: (r) => <StatusBadge value={r.state} /> },
    { key: "last_check", label: "Last Check", render: (r) => <span className="text-xs text-slate-500">{timeAgo(r.last_check)}</span> },
  ];

  return (
    <div>
      <PageHeader icon={Boxes} title="Asset Registry"
        subtitle="Centralized inventory of every protected website, API, database, server, cloud environment and AI model." actions={<DemoTag />} />
      <Table columns={columns} rows={data} testid="assets-table" onRowClick={(r) => setSel(r)} />

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{sel.name}</DialogTitle>
                <DialogDescription className="font-mono text-xs text-slate-500">{sel.id} · {sel.type} · {sel.environment}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Risk"><RiskBadge value={sel.risk_level} /></Info>
                <Info label="State"><StatusBadge value={sel.state} /></Info>
                <Info label="Owner"><span className="text-slate-300">{sel.owner}</span></Info>
                <Info label="Last incident"><span className="text-slate-300">{sel.last_incident ? timeAgo(sel.last_incident) : "None"}</span></Info>
              </div>
              <div className="mt-2">
                <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">Connected Integrations</p>
                <div className="flex flex-wrap gap-1.5">
                  {(sel.integrations || []).map((i) => <span key={i} className="rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 text-xs text-slate-300">{i}</span>)}
                </div>
              </div>
              <div className="mt-3">
                <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">Lifecycle Actions</p>
                <div className="flex flex-wrap gap-2">
                  {ACTIONS.map((a) => (
                    <button key={a.key} disabled={busy} onClick={() => act(a.key)} data-testid={`asset-action-${a.key.toLowerCase()}`}
                      className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm text-slate-200 hover:border-blue-500/40 hover:text-blue-300 disabled:opacity-50">
                      <a.icon className="h-4 w-4" /> {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
